// Capability gating for the portal UI.
//
// This hook is the single chokepoint the nav, route guards, and write actions
// consult. Capabilities resolve against the MANAGEMENT store (access-over-access
// for the Keycloak/OpenFGA/SPIFFE modules) via one batched check at load. The backends
// enforce regardless — this only decides what the UI shows.
//
// All gates resolve against management-store relations (owner/maintainer/viewer):
//   authorization.*  → store:access      (FGA config pages)
//   vocabulary.create → self-service (any authenticated user may create types;
//                      server-side removed the owner check from POST)
//
// Resource-type edit/delete is NOT a portal-wide capability: authority is held per
// type on resource_type:<slug>. See useEditableResourceTypes below.
//   governance.*     → store:management   (management-store pages)
//   spiffe.manage    → module:spiffe owner (approve/reject/decommission consumer
//                      clusters). Distinct from the Keycloak module since #368 —
//                      administering SPIFFE no longer implies Keycloak ownership.
//   platforms.* and requests.* are self-service — always granted; the
//   registry scopes platforms/clients/request-approvals per object server-side.
//
// The Companies page (customer onboarding) is NOT gated here: it needs admin on
// provider:intility in the access store, the same gate as keycloak-api. See useIsProviderAdmin.
//
// The access tree is NOT gated here: fga-api self-scopes every response (own
// covering roots free, expansions need reader on the prefix, another subject
// needs the top relation), so authentication is the only gate. /audit-log is
// ungated for the same reason.
import { useQuery } from "@tanstack/react-query";
import { checkRelationship } from "~/api/client";
import { batchCheckManagement, checkManagement } from "~/api/management";
import { auth } from "./index";

export type Capability =
  | "authorization.read"
  | "authorization.write"
  | "vocabulary.create"
  | "platforms.read"
  | "platforms.write"
  | "requests.read"
  | "requests.write"
  | "governance.read"
  | "governance.write"
  | "spiffe.manage";

const GATES: Record<string, { object: string; relation: string }> = {
  "authorization.read": { object: "store:access", relation: "viewer" },
  "authorization.write": { object: "store:access", relation: "maintainer" },
  "governance.read": { object: "store:management", relation: "viewer" },
  "governance.write": { object: "store:management", relation: "maintainer" },
  "spiffe.manage": { object: "module:spiffe", relation: "owner" },
};

/** `BatchCheckController` rejects a larger batch (OpenFGA `maxChecksPerBatchCheck`). */
const CHECK_BATCH_LIMIT = 50;

const SELF_SERVICE: ReadonlySet<Capability> = new Set([
  "platforms.read",
  "platforms.write",
  // Request approval rights are per-target-client and the pending endpoint
  // pre-filters to what the caller may decide — no portal-level gate.
  "requests.read",
  "requests.write",
  // Creating resource types is self-service — any authenticated user may add
  // new types. Editing and deleting is gated per type, not portal-wide.
  "vocabulary.create",
]);

export function useCapabilities() {
  const sub = auth.keycloak.tokenParsed?.sub;

  const { data } = useQuery({
    queryKey: ["capabilities", sub],
    enabled: Boolean(sub),
    // Grants change rarely mid-session; refetch on focus keeps revocations honest.
    staleTime: 60_000,
    queryFn: async () => {
      const entries = Object.entries(GATES);
      const response = await batchCheckManagement(
        entries.map(([capability, gate]) => ({
          tupleKey: {
            user: `user:${sub}`,
            relation: gate.relation,
            object: gate.object,
          },
          correlationId: capability.replace(".", "-"),
        })),
      );
      const granted: Record<string, boolean> = {};
      for (const [capability] of entries) {
        granted[capability] =
          response.result[capability.replace(".", "-")]?.allowed ?? false;
      }
      return granted;
    },
  });

  return {
    /** False until the batch check resolves — gated UI fails closed meanwhile. */
    loaded: data !== undefined,
    can: (capability: Capability): boolean =>
      SELF_SERVICE.has(capability) ? true : (data?.[capability] ?? false),
  };
}

/**
 * Per-type edit authority for the resource catalog. Mirrors
 * `CatalogGovernance.CanEditResourceTypeAsync`, which is `maintainer` on
 * `resource_type:<slug>` **OR** `owner` on `store:access`:
 *
 * - `owner` cascades to `maintainer` for the type's creator, and `store:access#owner`
 *   cascades through the type's `store` parent tuple, so IAM keeps the global override.
 * - The explicit `store:access#owner` leg matters for types created before per-type
 *   governance: they have no parent tuple, so nothing cascades and the per-type check
 *   alone would hide the buttons from the only people who can still edit them.
 *
 * One batched request per rendered list; the backend enforces regardless.
 */
export function useEditableResourceTypes(slugs: readonly string[]) {
  const sub = auth.keycloak.tokenParsed?.sub;
  // Sorted join: the slug list is rebuilt on every render, so the key must depend
  // on contents rather than array identity.
  const slugKey = [...slugs].sort().join(",");

  const { data } = useQuery({
    queryKey: ["resource-type-authority", sub, slugKey],
    enabled: Boolean(sub) && slugs.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      // The type list is unpaged (server default 100) but batch-check rejects more than
      // CHECK_BATCH_LIMIT, so chunk it — a single oversized request would 400 and hide the
      // buttons from everyone, IAM included. The store:access leg rides in its own request
      // so a full chunk stays within the limit.
      const chunks: string[][] = [];
      for (let i = 0; i < slugs.length; i += CHECK_BATCH_LIMIT) {
        chunks.push(slugs.slice(i, i + CHECK_BATCH_LIMIT));
      }

      // Checked first and on its own: an affirmative answer makes the per-type checks
      // moot, so IAM costs one request rather than one per chunk.
      const storeOwner = await checkManagement({
        tupleKey: {
          user: `user:${sub}`,
          relation: "owner",
          object: "store:access",
        },
      });
      if (storeOwner.allowed) return new Set(slugs);

      const responses = await Promise.all(
        chunks.map((chunk, c) =>
          batchCheckManagement(
            // Index is the correlation id — slugs are safe here, but this matches the
            // pattern FilterViewableBlueprintsAsync uses server-side.
            chunk.map((slug, i) => ({
              tupleKey: {
                user: `user:${sub}`,
                relation: "maintainer",
                object: `resource_type:${slug}`,
              },
              correlationId: String(c * CHECK_BATCH_LIMIT + i),
            })),
          ),
        ),
      );

      const granted = new Set<string>();
      responses.forEach((response, c) => {
        chunks[c].forEach((slug, i) => {
          if (response.result[String(c * CHECK_BATCH_LIMIT + i)]?.allowed) {
            granted.add(slug);
          }
        });
      });
      return granted;
    },
  });

  return {
    /** False until the batch check resolves — gated buttons fail closed meanwhile. */
    canEdit: (slug: string): boolean => data?.has(slug) ?? false,
  };
}

export function useIsProviderAdmin() {
  const sub = auth.keycloak.tokenParsed?.sub;

  const { data, isLoading } = useQuery({
    queryKey: ["provider-admin", sub],
    enabled: Boolean(sub),
    staleTime: 60_000,
    queryFn: async () => {
      const result = await checkRelationship({
        tupleKey: {
          user: `user:${sub}`,
          relation: "admin",
          object: "provider:intility",
        },
      });
      return result.allowed ?? false;
    },
  });

  return { isProviderAdmin: data ?? false, isLoading };
}

export function useIsKeycloakModuleOwner() {
  const sub = auth.keycloak.tokenParsed?.sub;

  const { data, isLoading } = useQuery({
    queryKey: ["keycloak-module-owner", sub],
    enabled: Boolean(sub),
    staleTime: 60_000,
    queryFn: async () => {
      const result = await checkManagement({
        tupleKey: {
          user: `user:${sub}`,
          relation: "owner",
          object: "module:keycloak",
        },
      });
      return result.allowed ?? false;
    },
  });

  return { isKeycloakModuleOwner: data ?? false, isLoading };
}
